import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { hasFeature, featureLabel, FEATURE_META } from "../lib/features";
import { money } from "../lib/pricing";
import API from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { LockKey, ArrowRight, Sparkle, Timer } from "@phosphor-icons/react";

let catalogPromise = null;
export function loadCatalog() {
  if (!catalogPromise) catalogPromise = API.get("/billing/catalog").then(({ data }) => data).catch(() => { catalogPromise = null; return null; });
  return catalogPromise;
}

export function LockedFeature({ feature, compact = false }) {
  const { user, checkAuth } = useAuth();
  const [addon, setAddon] = useState(null);
  const [trialDays, setTrialDays] = useState(14);
  const [busy, setBusy] = useState(false);
  useEffect(() => { loadCatalog().then((c) => { setAddon(c?.addons?.find((a) => a.key === feature) || null); if (c?.trial_days) setTrialDays(c.trial_days); }); }, [feature]);
  const meta = FEATURE_META[feature] || {};
  const canBuy = ["super_admin", "admin"].includes(user?.role);
  const trial = user?.addon_trials?.[feature];
  const trialAvailable = !trial && addon && addon.sellable !== false && addon.trialable !== false && !user?.is_platform_admin;
  const endedOn = trial?.status === "ended" && trial.ends_at ? new Date(trial.ends_at).toLocaleDateString("en-GB") : null;

  const startTrial = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`/billing/trials/${feature}`);
      toast.success(`${featureLabel(feature)} is on — free until ${new Date(data.ends_at).toLocaleDateString("en-GB")}`);
      await checkAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start the trial — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`border border-dashed border-border rounded-xl bg-muted/20 ${compact ? "p-5" : "p-8 sm:p-12 max-w-2xl mx-auto mt-6"}`} data-testid={`locked-feature-${feature}`}>
      <div className="flex items-start gap-4">
        <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <LockKey size={22} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground mb-1">Add-on not in your plan</p>
          <h2 className="text-lg md:text-xl font-bold font-[Chivo] tracking-tight">{featureLabel(feature)}</h2>
          <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{addon?.blurb || meta.blurb}</p>
          {endedOn && <p className="text-xs text-amber-700 dark:text-amber-400 mt-2" data-testid="locked-feature-trial-ended">Your free trial ended on {endedOn}. Everything you set up is still here — add it to your plan to carry on.</p>}
          <div className="flex flex-wrap items-center gap-3 mt-5">
            {addon && addon.sellable !== false && (
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold" data-testid="locked-feature-price">
                <Sparkle size={14} className="text-primary" weight="fill" /> {money(addon.monthly_price, { compact: true })}<span className="text-muted-foreground font-normal text-xs">/month, added to your plan</span>
              </span>
            )}
            {canBuy ? (
              <>
                {trialAvailable && (
                  <Button size="sm" variant="outline" onClick={startTrial} disabled={busy} data-testid="locked-feature-trial-btn">
                    <Timer size={14} className="mr-1" /> {busy ? "Switching on…" : `Try free for ${trialDays} days`}
                  </Button>
                )}
                <Button asChild size="sm">
                  <Link to={`/platform/billing?add=${feature}`} data-testid="locked-feature-cta">Add to plan <ArrowRight size={14} className="ml-1" /></Link>
                </Button>
              </>
            ) : (
              <span className="text-xs text-muted-foreground" data-testid="locked-feature-ask-admin">
                Ask your workspace admin to add this from Billing{trialAvailable ? ` — they can switch on a free ${trialDays}-day trial straight from this page` : ""}.
              </span>
            )}
          </div>
          {canBuy && trialAvailable && <p className="text-[11px] text-muted-foreground mt-3" data-testid="locked-feature-trial-note">No card needed. One trial per add-on; when it ends the module locks again and nothing is deleted.</p>}
        </div>
      </div>
    </div>
  );
}

export default function FeatureGuard({ feature, children }) {
  const { user } = useAuth();
  if (hasFeature(user, feature)) return children;
  return <LockedFeature feature={feature} />;
}
